import React from 'react';
import { MessageSquare, Send, AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { BOUTON_PIED_CLASSES, MODAL_CONTENT_CLASSES } from '../nosDepensesUtils';

/**
 * Observation ou demande de justificatif sur une transaction.
 *
 * Monté sur les primitives `Dialog` : le voile maison de `MODAL_OVERLAY_Z40_CLASS`
 * était rendu hors portail, donc neutralisé par le `pointer-events: none` que
 * Radix pose sur le `body` dès qu'un autre `Dialog` est ouvert.
 *
 * L'ambre remplace le jaune de l'avertissement : DESIGN-SYSTEM.md réserve
 * l'ambre à ce qui alerte sans empêcher d'agir.
 */
const ObservationModal = ({ isOpen, onClose, observation, setObservation, isLoading, onSubmit }) => {
  const estJustificatif = observation?.type === 'justificatif';

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(ouvert) => !ouvert && onClose?.()}>
      <DialogContent className={`${MODAL_CONTENT_CLASSES} max-w-2xl`}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#c66d59] text-white">
            <MessageSquare size={18} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {estJustificatif ? 'Demander un justificatif' : 'Ajouter une observation'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Le gestionnaire recevra une notification et devra répondre avant la validation finale.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {estJustificatif ? (
            <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertCircle size={16} />
                Justificatif manquant
              </p>
              <p className="mt-2 text-sm text-amber-800">
                Cette transaction ne porte aucune pièce. Demandez au gestionnaire de fournir
                le document correspondant.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border-l-4 border-[#246990] bg-[#eef7fb] p-4">
              <p className="text-sm text-gray-700">
                Ajoutez une observation ou demandez des éclaircissements sur cette transaction.
              </p>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={observation?.message || ''}
              onChange={(e) => setObservation({ ...observation, message: e.target.value })}
              placeholder={estJustificatif
                ? 'Ex : veuillez fournir la facture ou le reçu correspondant à cette dépense…'
                : 'Ex : le montant semble élevé, pouvez-vous fournir plus de détails ?'}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            />
          </div>
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button
            variant="outline"
            className={BOUTON_PIED_CLASSES}
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            className={`${BOUTON_PIED_CLASSES} bg-[#c66d59] text-white hover:bg-[#ae7467]/90`}
            onClick={onSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Envoi…
              </>
            ) : (
              <>
                <Send size={16} />
                Envoyer
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ObservationModal;
